import { storage } from '@/services/storage'
import { cookie } from '@/services/cookie'
import { auth as Auth, user as User } from '@/services/axios'
import router from '@/routers'

export const auth = {
    namespaced: true,
    state: {
        user: null,
        token: null,
        tokenV2: null,
        isRefreshing: false,
        isRefreshingV2: false,
        unauthorizedPermissions: [],
        refreshToken: null,
        refreshTokenV2: null
    },
    getters: {
        getUser(state) {
            return state.user
        },
        getToken(state) {
            return state.token
        },
        getTokenV2(state) {
            return state.tokenV2
        },
        getUnauthorizedPermissions(state) {
            return state.unauthorizedPermissions
        },
        getRefreshToken(state) {
            return state.refreshToken
        },
        isRefreshingV2(state) {
            return state.isRefreshingV2
        }

    },
    mutations: {
        SET_USER(state, payload) {
            state.user = payload
        },
        SET_TOKEN(state, payload) {
            state.token = payload
        },
        SET_TOKEN_V2(state, payload) {
            state.tokenV2 = payload
        },
        SET_IS_REFRESHING(state, payload) {
            state.isRefreshing = payload
        },
        SET_IS_REFRESHING_V2(state, payload) {
            state.isRefreshingV2 = payload
        },
        SET_UNAUTHORIZED_PERMISSION(state, payload) {
            if (!state.unauthorizedPermissions.includes(payload)) state.unauthorizedPermissions.push(payload)
        },
        CLEAR_UNAUTHORIZED_PERMISSIONS(state) {
            state.unauthorizedPermissions = []
        },
        SET_REFRESH_TOKEN(state, payload) {
            state.refreshToken = payload
        }
    },
    actions: {

        /**
         * Action que seta os dados do usuário logado no state 
         */
        setUser({ commit }, payload) {
            commit('SET_USER', payload)
        },

        /**
         * Action que seta o token JWT no state 
         */
        setToken({ commit }, payload) {
            commit('SET_TOKEN', payload)
        },

        /**
         * Action que seta o token JWT da API V2 no state 
         */
        setTokenV2({ commit }, payload) {
            commit('SET_TOKEN_V2', payload)
        },

        /**
         * Action que define no state que o processo de refresh do token JWT está
         * sendo efetuado 
         */
        setIsRefreshing({ commit }, payload) {
            commit('SET_IS_REFRESHING', payload)
        },
        /**
         * Action que define no state que o processo de refresh do token JWT está
         * sendo efetuado na API V2
         */
        setIsRefreshingV2({ commit }, payload) {
            commit('SET_IS_REFRESHING_V2', payload)
        },

        /**
         * Action que seta no state uma nova permissão à qual o usuário logado não possui 
         */
        setUnauthorizedPermission({ commit }, payload) {
            commit('SET_UNAUTHORIZED_PERMISSION', payload)
        },

        /**
         * Action que remove do state as informações referentes as permissões que o
         * usuário logado não possui
         */
        clearUnauthorizedPermissions({ commit }) {
            commit('CLEAR_UNAUTHORIZED_PERMISSIONS')
        },

        /**
         * Realiza o processo de login
         * 
         * @param {*} dispatch
         * @param {*} data 
         */
        async doLogin({ dispatch }, data) {
            try {
                const response = await Auth.loginV2(data);
                const userTokenV2 = response.data.token;
                
                const getToken = await Auth.login(data);
                const userToken = getToken.data.token;
                const refreshToken = getToken.data.refreshToken;


                // 🔐 Verifica se precisa redirecionar para 2FA
                if (response.data.valid) {
                    const encoded = {
                        email: btoa(data.email),
                        password: btoa(data.password),
                    };

                    storage.set('auth_twofactor_data', JSON.stringify(encoded));
                    router.push({ path: '/autenticacao-dois-fatores' });
                    return response;
                }

                // 🔄 Caso seja apenas solicitação de código (passo 1 do 2FA)
                if (response.data.message === 'Código gerado com sucesso.') {
                    return response;
                }

                // ✅ Login completo
                const user = await User.me(userToken);
                const okResponse = await Auth.loginOk(data);
                if (
                    typeof okResponse.data === 'object' &&
                    Object.prototype.hasOwnProperty.call(okResponse.data, 'goscore-token-user')
                    ) {
                    for (const [key, value] of Object.entries(okResponse.data)) {
                        if (key !== 'goscore-token-user') storage.setLegacy(key, value);
                    }
                }

                dispatch('setToken', userToken);
                dispatch('setRefreshToken', refreshToken);
                dispatch('setTokenV2', userTokenV2);
                dispatch('setUser', user.data);
                dispatch('app/setCompaniesRestricted', null, { root: true });

                user.data.photo = user.data.photo
                ? user.data.photo + '?' + new Date().getTime()
                : require('@/assets/imgs/no-avatar.png');

                const userData = JSON.stringify(user.data);

                storage.set('user_logged', userData);
                storage.set('user_token', userToken);
                storage.set('user_refresh_token', refreshToken);
                storage.set('user_token_v2', userTokenV2);

                router.push({ path: '/' });
            } catch (error) {
                throw error;
            }
        },


        /**
         * Seta os dados do usuário logado salvos no localStorage no store
         * 
         * @param {*} dispatch
         */
        async authExists({ dispatch }) {
            const user = storage.get('user_logged')
            const token = storage.get('user_token')
            const refreshToken = storage.get('user_refresh_token')
            const tokenV2 = storage.get('user_token_v2')
            if (!user) return false

            let userdata = JSON.parse(user)

            dispatch('setToken', token)
            dispatch('setRefreshToken', refreshToken)
            dispatch('setTokenV2', tokenV2)
            dispatch('setUser', userdata)
            await dispatch('app/setCompaniesRestricted', null, { root: true })

            return true

        },

        /**
         * Limpa os dados do usuário do store e do localStorage
         * 
         * @param {*} dispatch 
         */
        async clearUserData({ dispatch }) {
            dispatch('setUser', null)
            dispatch('setRefreshToken', null)
            dispatch('setToken', null)
            dispatch('setTokenV2', null)
            await storage.clear()
            await cookie.deleteAllCookies()
            dispatch('dashboardFilter/resetFilters', null, { root: true });
            router.push({ path: '/login' }).catch(() => { })
        },

        /**
         * Realiza logout do usuário
         * 
         * @param {*} dispatch
         */
        async logout({ dispatch }) {
            const token = storage.get('user_token');
            await Auth.logout(token)
                .then(() => {
                    dispatch('clearUserData')
                })
        },

        /**
         * Realiza uma requisição para /me de modo a verificar se o token
         * salvo é válido
         * Valida só o token da v1
         * @param {*} dispatch
         */
        async validatedToken({ dispatch }) {
            const token = storage.get('user_token');
            if (token) {
                try {
                    await User.me(token)
                } catch (error) {
                    dispatch('clearUserData')
                }
            }
        },

        /**
         * Action que seta o Refresh Token do usuário no local storage e no stage
         *
         * @param {String} payload 
         */
        setRefreshToken({ commit }, payload) {
            commit('SET_REFRESH_TOKEN', payload)
        }
    }
}

